import { type LawFields, LawFieldsService, type TUser } from '@/types/generated'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { lawTopicsFR } from '@/i18n/lawTopics'
import { lawFieldsDE, lawFieldsEN, lawFieldsFR } from '@/i18n/lawFields'
import { useI18n } from 'vue-i18n'

export const useLawFieldStore = defineStore('lawFieldStore', () => {
  const lawFields = ref<LawFields | null>(null)

  const fetchLawFields = async () => {
    if (!lawFields.value) {
      lawFields.value = await LawFieldsService.lawFieldsRetrieve()
    }
  }

  const getLawFieldOptions = (user: TUser, locale?: string) => {
    const lawFieldsList = user?.org.law_fields || []
    const currentLocale = locale || useI18n().locale.value

    return (lawFieldsList as string[]).map((lawField) => {
      let translatedTitle = lawField

      // Apply translations based on locale, fallback to German key
      if (currentLocale === 'fr') {
        translatedTitle = lawFieldsFR[lawField] || lawField
      } else if (currentLocale === 'en') {
        translatedTitle = lawFieldsEN[lawField] || lawField
      } else {
        translatedTitle = lawFieldsDE[lawField] || lawField
      }

      return {
        value: lawField,
        title: translatedTitle
      }
    })
  }

  const getLawTopicOptions = (lawField: string, locale?: string) => {
    if (!lawFields.value) return []
    if (!lawField) return []

    const currentLocale = locale || useI18n().locale.value

    const mappedOptions = (
      lawFields.value?.data[lawField]?.map((topic) => {
        // Apply French translations for law topics, fallback to German key
        let translatedTitle = topic
        if (currentLocale === 'fr') {
          translatedTitle = lawTopicsFR[topic] || topic
        }
        // For other locales, keep the German key

        return {
          value: topic,
          title: translatedTitle
        }
      }) || []
    ).sort((a, b) => a.title.localeCompare(b.title))

    return mappedOptions
  }

  return {
    lawFields,
    fetchLawFields,
    getLawTopicOptions,
    getLawFieldOptions
  }
})
